import { useCallback, useEffect, useState } from "react";

/** What travels with the approval (#7440 AC6): the check's key and the line the card showed. */
export interface AcknowledgementInput {
  check: string;
  detail: string;
}

/**
 * Browser-held acknowledgements for the "Before you approve" rail (#7432 AC4,
 * #7440 AC6).
 *
 * Decided 2026-10-12: NOT persisted before approve. A reviewer who clicks
 * "Differences are expected" has made a judgement about THIS extraction run,
 * so the set resets when the transformation changes (a reprocess produces
 * different rows) and a reload mid-review clears it.
 *
 * This map is the reviewer's CLICKS, keyed by check with the detail they saw.
 * It is not what the approval sends: `buildReviewChecks` honours an entry
 * only while the card's current detail still equals the stored one, and
 * returns the honoured set (`ReviewChecks.acknowledgements`) — that is the
 * request body, so a stale click never travels (!4936 review).
 */
export const useReviewAcknowledgements = (transformationId: string | undefined) => {
  const [acknowledged, setAcknowledged] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    setAcknowledged(new Map());
  }, [transformationId]);

  const acknowledge = useCallback((check: string, detail: string) => {
    setAcknowledged((prev) => {
      const next = new Map(prev);
      next.set(check, detail);
      return next;
    });
  }, []);

  const undo = useCallback((check: string) => {
    setAcknowledged((prev) => {
      if (!prev.has(check)) return prev;
      const next = new Map(prev);
      next.delete(check);
      return next;
    });
  }, []);

  return { acknowledged, acknowledge, undo };
};
